import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import { Check, X, Loader2, ArrowLeft, Download, MessageSquare } from 'lucide-react';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  employee_count: string;
  company: string;
  country_region: string;
  phone: string;
  email: string;
  website: string | null;
  message: string;
  created_at: string;
  status: string;
}

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [yearFilter, setYearFilter] = useState<string>('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (yearFilter === 'all') {
      setFilteredContacts(contacts);
    } else {
      const targetYear = parseInt(yearFilter);
      const filtered = contacts.filter(contact => {
        const date = new Date(contact.created_at);
        const year = date.getUTCFullYear();
        return year === targetYear;
      });
      setFilteredContacts(filtered);
    }
  }, [contacts, yearFilter]);

  const fetchContacts = async () => {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContacts(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      const updatedContacts = contacts.map(contact =>
        contact.id === id ? { ...contact, status } : contact
      );
      setContacts(updatedContacts);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadCSV = () => {
    const headers = [
      'Date',
      'First Name',
      'Last Name',
      'Title',
      'Company',
      'Country/Region',
      'Phone',
      'Email',
      'Website',
      'Message',
      'Status'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredContacts.map(contact => [
        formatDate(contact.created_at),
        contact.first_name,
        contact.last_name,
        contact.title,
        contact.company,
        contact.country_region,
        contact.phone,
        contact.email,
        contact.website || '',
        `"${contact.message.replace(/"/g, '""')}"`,
        contact.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `contact-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl font-bold text-gray-900">Contact Submissions</h1>
            </div>
            
            {contacts.length > 0 && (
              <button
                onClick={downloadCSV}
                className="flex items-center gap-2 bg-primary bg-opacity-10 text-primary px-4 py-2 rounded-lg hover:bg-opacity-20 transition-colors"
                title="Download all submissions as CSV"
              >
                <Download className="w-5 h-5" />
                Export Contact Submissions (CSV)
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          {!loading && contacts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-primary text-2xl font-bold">{contacts.length}</div>
                <div className="text-gray-600 text-sm">Total Submissions</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-primary text-2xl font-bold">
                  {contacts.filter(c => new Date(c.created_at).getUTCFullYear() === 2025).length}
                </div>
                <div className="text-gray-600 text-sm">2025 Submissions</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-primary text-2xl font-bold">
                  {contacts.filter(c => new Date(c.created_at).getUTCFullYear() === 2026).length}
                </div>
                <div className="text-gray-600 text-sm">2026 Submissions</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <label className="block text-gray-600 text-sm mb-2">Filter by Year</label>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                >
                  <option value="all">All Years</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </select>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="bg-white rounded-lg p-6 text-center text-gray-600">
              No contact submissions yet.
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="bg-white rounded-lg p-6 text-center text-gray-600">
              No submissions found for the selected year.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredContacts.map((contact) => (
                        <tr 
                          key={contact.id} 
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedContact?.id === contact.id ? 'bg-[#40E0D0]/5' : ''}`}
                          onClick={() => setSelectedContact(contact)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(contact.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {contact.first_name} {contact.last_name}
                            </div>
                            <div className="text-sm text-gray-600">{contact.title}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{contact.company}</div>
                            <div className="text-sm text-gray-600">{contact.country_region}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{contact.email}</div>
                            <div className="text-sm text-gray-600">{contact.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              contact.status === 'completed' 
                                ? 'bg-green-100 text-green-800' 
                                : contact.status === 'in_progress'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {contact.status === 'completed' 
                                ? 'Completed' 
                                : contact.status === 'in_progress'
                                ? 'In Progress'
                                : 'New'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(contact.id, 'in_progress');
                                }}
                                className="text-yellow-500 hover:text-yellow-400"
                                title="Mark as In Progress"
                              >
                                <Loader2 className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(contact.id, 'completed');
                                }}
                                className="text-green-500 hover:text-green-400"
                                title="Mark as Completed"
                              >
                                <Check className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(contact.id, 'new');
                                }}
                                className="text-gray-500 hover:text-gray-600"
                                title="Mark as New"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold text-gray-900">Message Details</h2>
                </div>
                
                {selectedContact ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-600">From</h3>
                      <p className="text-gray-900">{selectedContact.first_name} {selectedContact.last_name}</p>
                      <p className="text-gray-600">{selectedContact.title} at {selectedContact.company}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-600">Contact Information</h3>
                      <p className="text-gray-900">{selectedContact.email}</p>
                      <p className="text-gray-900">{selectedContact.phone}</p>
                      {selectedContact.website && (
                        <a 
                          href={selectedContact.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {selectedContact.website}
                        </a>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-600">Message</h3>
                      <p className="text-gray-900 whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-600">Additional Details</h3>
                      <p className="text-gray-900">Employee Count: {selectedContact.employee_count}</p>
                      <p className="text-gray-900">Location: {selectedContact.country_region}</p>
                      <p className="text-gray-900">Submitted: {formatDate(selectedContact.created_at)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-600 py-8">
                    Select a contact to view message details
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}